import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { Card, PillButton, Input, SegmentedControl, Chip } from '../../ui';
import { ReactComponent as UploadIcon } from '../../assets/icons/upload.svg';
import PlanLocked from './PlanLocked';
import './AdminLayout.css';
import { apiErrorMessage } from '../../utils/apiError';

const FEATURES = ['share', 'totp', 'files', 'breachCheck', 'extension', 'mobileApp'];
const LOGIN_METHODS = ['card', 'nfc', 'email', 'master_password'];
const MAX_LOGO_BYTES = 200 * 1024;

function Branding() {
  const { t } = useTranslation();
  const { org, has, reloadOrg, orgId } = useOutletContext();
  const canManage = has('branding.manage');
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    appName: '', logoUrl: '', primaryColor: '#FFFFFF', accentColor: '#6E8BFF',
    theme: 'dark', customDomain: '', features: {}, loginMethods: [...LOGIN_METHODS],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    if (org?.branding) {
      setForm((prev) => ({ ...prev, ...org.branding }));
    }
  }, [org]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      setError(t('admin.branding.logoTooBig', { kb: MAX_LOGO_BYTES / 1024 }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setField('logoUrl', reader.result);
    reader.readAsDataURL(file);
  };

  const toggleFeature = (key) => {
    setForm((prev) => ({ ...prev, features: { ...prev.features, [key]: !prev.features?.[key] } }));
  };

  const toggleLoginMethod = (method) => {
    setForm((prev) => {
      const has_ = prev.loginMethods.includes(method);
      return { ...prev, loginMethods: has_ ? prev.loginMethods.filter((m) => m !== method) : [...prev.loginMethods, method] };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.put(`/org/${orgId}/branding`, form);
      setSavedAt(Date.now());
      reloadOrg();
    } catch (err) {
      setError(apiErrorMessage(err, t, 'errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return <p className="sgc-admin__muted">{t('admin.noPermission')}</p>;
  }

  if (org.plan !== 'business') {
    return (
      <div>
        <h2 className="sgc-admin__section-title">{t('admin.tabs.branding')}</h2>
        <PlanLocked orgId={orgId} canBilling={has('billing.manage')} />
      </div>
    );
  }

  return (
    <div>
      <h2 className="sgc-admin__section-title">{t('admin.tabs.branding')}</h2>
      {error && <p className="sgc-admin__error">{error}</p>}
      {savedAt && !error && <p className="sgc-admin__muted">{t('admin.policies.saved')}</p>}

      <form className="sgc-admin__branding-grid" onSubmit={handleSave}>
        <Card className="sgc-admin__form">
          <Input label={t('admin.branding.appName')} value={form.appName || ''} onChange={(e) => setField('appName', e.target.value)} />

          <div>
            <label className="sgc-admin__muted">{t('admin.branding.logo')}</label>
            <div className="sgc-admin__row-actions">
              {form.logoUrl && <img src={form.logoUrl} alt="" style={{ height: 32 }} />}
              <PillButton type="button" size="sm" variant="secondary" icon={UploadIcon} onClick={() => fileRef.current?.click()}>
                {t('admin.branding.uploadLogo')}
              </PillButton>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleLogoFile} />
            </div>
          </div>

          <div className="sgc-admin__color-input">
            <input type="color" className="sgc-admin__color-swatch" value={form.primaryColor || '#FFFFFF'} onChange={(e) => setField('primaryColor', e.target.value)} />
            <Input label={t('admin.branding.primaryColor')} value={form.primaryColor || ''} onChange={(e) => setField('primaryColor', e.target.value)} />
          </div>
          <div className="sgc-admin__color-input">
            <input type="color" className="sgc-admin__color-swatch" value={form.accentColor || '#6E8BFF'} onChange={(e) => setField('accentColor', e.target.value)} />
            <Input label={t('admin.branding.accentColor')} value={form.accentColor || ''} onChange={(e) => setField('accentColor', e.target.value)} />
          </div>

          <div>
            <label className="sgc-admin__muted">{t('admin.branding.theme')}</label>
            <SegmentedControl
              options={['dark', 'light', 'system'].map((v) => ({ value: v, label: t(`admin.branding.themes.${v}`) }))}
              value={form.theme}
              onChange={(v) => setField('theme', v)}
            />
          </div>

          <Input
            label={t('admin.branding.customDomain')}
            value={form.customDomain || ''}
            onChange={(e) => setField('customDomain', e.target.value)}
            placeholder={t('admin.branding.customDomainPlaceholder')}
          />
          <p className="sgc-admin__muted">{t('admin.branding.dnsInstructions')}</p>

          <div>
            <label className="sgc-admin__muted">{t('admin.branding.features')}</label>
            <div className="sgc-admin__cidr-list">
              {FEATURES.map((f) => (
                <Chip key={f} label={t(`admin.branding.featureNames.${f}`)} selected={!!form.features?.[f]} onClick={() => toggleFeature(f)} />
              ))}
            </div>
          </div>

          <div>
            <label className="sgc-admin__muted">{t('admin.branding.loginMethods')}</label>
            <div className="sgc-admin__cidr-list">
              {LOGIN_METHODS.map((m) => (
                <Chip key={m} label={t(`admin.branding.loginMethodNames.${m}`)} selected={form.loginMethods.includes(m)} onClick={() => toggleLoginMethod(m)} />
              ))}
            </div>
          </div>

          <div className="sgc-admin__form-actions">
            <PillButton type="submit" disabled={saving}>{saving ? t('common.loading') : t('common.save')}</PillButton>
          </div>
        </Card>

        <div>
          <p className="sgc-admin__muted">{t('admin.branding.preview')}</p>
          <div
            className="sgc-admin__preview"
            style={{ background: form.theme === 'light' ? '#F4F4F5' : '#0B0C0E', color: form.theme === 'light' ? '#111214' : '#F2F2F3' }}
          >
            {form.logoUrl ? (
              <img src={form.logoUrl} alt="" className="sgc-admin__preview-logo" />
            ) : (
              <strong>{form.appName || 'App'}</strong>
            )}
            <p style={{ margin: 0, fontSize: 13, opacity: 0.7 }}>{t('login.subtitle')}</p>
            <button
              type="button" className="sgc-admin__preview-btn"
              style={{ background: form.primaryColor || '#FFFFFF', color: form.theme === 'light' ? '#fff' : '#111214' }}
            >
              {t('login.title')}
            </button>
            <span style={{ color: form.accentColor, fontSize: 12 }}>{form.customDomain || 'app.securegatecard.nordicbyte.co.uk'}</span>
          </div>
        </div>
      </form>
    </div>
  );
}

export default Branding;
